import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { classnames } from 'tailwindcss-classnames';
import * as Yup from 'yup';

import ArrowNormal from '../../assets/svg/arrow-normal.svg';
import { COLORS } from '../../constants/colors';
import {
  NEWSLETTER_GROUPS_DISPLAY,
  NEWSLETTER_GROUPS_ID,
} from '../../constants/newsletter';
import Spinner from '../Global/Spinner';
import ExternalLink from '../Link/ExternalLink';

const StinkNewsletterForm = ({ group = 'courierWeekly', placeholder = '' }) => {
  const [isLoading, setLoading] = useState(false);
  const [code, setCode] = useState('idle');
  const [response, setResponse] = useState('');
  const [input, setInput] = useState('');
  const [isFocused, setFocused] = useState(false);

  const hasError = code === 'error';
  const isSuccess = code === 'success';

  const validationSchema = Yup.string()
    .email('Please enter a valid email address.')
    .required('Please enter a valid email address.');

  const handleChange = (e) => {
    setInput(e.target.value);
    setCode('idle');
    setResponse('');
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleMailchimp = async () => {
    try {
      await axios({
        data: { email: input, groupId: NEWSLETTER_GROUPS_ID[group] },
        method: 'put',
        url: '/content/api/mailchimp',
      });
      setCode('success');
      setInput('');
      // reset
      setTimeout(() => {
        setCode('idle');
      }, 7500);
    } catch (error) {
      setResponse('We are currently unable to subscribe your email.');
      setCode('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    validationSchema
      .validate(input)
      .then(async () => {
        setLoading(true);
        await handleMailchimp();
        setLoading(false);
      })
      .catch((error) => {
        setResponse(error.message);
        setCode('error');
      });
  };

  const successText = (
    <div>
      <h5 className="stink-h5">You&apos;re all set.</h5>
      <p className="mt-2 stink-body-1 text-slate">
        {`Keep a lookout for your first issue of ${NEWSLETTER_GROUPS_DISPLAY[group]}, coming this
    Friday.`}
      </p>
    </div>
  );

  const legalText = (
    <p className="mt-2 stink-footnote text-slate">
      <span>{`You can unsubscribe at any time using the link in our emails. For
more details, review our `}</span>
      <ExternalLink
        className="stink-footnote"
        href="https://couriermedia.com/legal/privacy-policy/"
      >
        privacy policy
      </ExternalLink>
      .
    </p>
  );

  const errorText = (
    <p
      className={classnames(
        'transition-opacity',
        'mt-2',
        'stink-footnote',
        'text-poppy',
        'min-h-[14px]',
        { 'opacity-0': !hasError },
      )}
    >
      {response}
    </p>
  );

  const inputClasses = classnames(
    'py-4',
    'border-b',
    'outline-none',
    'w-full',
    'bg-transparent',
    { 'border-smoke': !hasError && !isFocused },
    { 'border-poppy': hasError },
    { 'border-licorice': isFocused },
    'stink-body-3',
    'transition-colors',
  );

  const form = (
    <form noValidate onSubmit={(e) => handleSubmit(e)}>
      <div className="relative">
        <input
          aria-label={placeholder}
          className={inputClasses}
          onBlur={handleBlur}
          onChange={(e) => handleChange(e)}
          onFocus={handleFocus}
          placeholder={placeholder}
          type="email"
          value={input}
        />
        <div className="absolute top-0 bottom-0 right-0 flex w-5.5 my-auto">
          {isLoading ? (
            <div className="flex items-center h-full">
              <Spinner color={COLORS.slate} />
            </div>
          ) : (
            <button
              className="w-full"
              data-component-id="stink_news_letter_form"
              type="button"
            >
              <ArrowNormal
                aria-label="Submit"
                className="fill-current text-slate"
              />
            </button>
          )}
        </div>
      </div>

      {errorText}
      {legalText}
    </form>
  );

  return (
    <div className="w-full min-h-[105px]">{isSuccess ? successText : form}</div>
  );
};

StinkNewsletterForm.propTypes = {
  group: PropTypes.oneOf(['courierWeekly', 'courierWorkshop']),
  placeholder: PropTypes.string,
};

export default StinkNewsletterForm;
